import { Component, type ReactNode } from 'react';
import { isChunkLoadError, reloadOnceForStaleChunk } from './ChunkReloadBoundary';
import { clearAppNamespace } from '@/lib/localStorage';

/**
 * Top-level boot/render safety net (13 JUN 2026).
 *
 * Mounted in main.tsx OUTSIDE every context provider, so a crash while a provider
 * initialises from localStorage (e.g. a save predating a schema change) can no
 * longer blank the whole app — the symptom behind the «white screen that survives
 * a refresh but works in incognito» reports. (Incognito starts with empty storage,
 * so it boots fine; a normal profile keeps re-loading the same corrupt state and
 * keeps crashing.) The in-App ChunkReloadBoundary only covers lazy screens BELOW
 * the providers, so it cannot catch this.
 *
 * Recovery ladder:
 *   1. chunk-shaped error → one-time hard reload (stale bundle after a deploy).
 *   2. any other crash → ONE automatic reset of the `qastudio.v1.*` app namespace
 *      + reload. This clears corrupt app state but PRESERVES the Supabase auth
 *      token (`sb-*`), so the user is NOT logged out and progress re-syncs from the
 *      server. Guarded by a sessionStorage flag kept OUTSIDE the cleared namespace
 *      so it runs at most once per tab.
 *   3. if it still crashes after the reset → show a manual recovery card instead of
 *      looping, so a genuine render bug isn't masked by an infinite reset cycle.
 */

// sessionStorage key · deliberately NOT under `qastudio.v1.` so clearAppNamespace()
// can't wipe it (which would let the auto-reset loop forever).
const AUTO_RESET_FLAG = '__qa_boot_reset';

function hasAutoReset(): boolean {
  try {
    return sessionStorage.getItem(AUTO_RESET_FLAG) === '1';
  } catch {
    return false;
  }
}
function markAutoReset(): void {
  try {
    sessionStorage.setItem(AUTO_RESET_FLAG, '1');
  } catch {
    /* sessionStorage blocked — auto-reset just won't be loop-guarded */
  }
}

interface Props {
  children: ReactNode;
}
interface State {
  // 'ok' → render children · 'recovering' → reset+reload in flight (render a brief
  // note, NOT children, so they don't immediately re-throw) · 'failed' → manual card.
  phase: 'ok' | 'recovering' | 'failed';
}

export class RootBoundary extends Component<Props, State> {
  override state: State = { phase: 'ok' };

  static getDerivedStateFromError(): State {
    return { phase: 'recovering' };
  }

  override componentDidCatch(error: unknown): void {
    // Stale bundle after deploy → just reload to fetch the fresh build.
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk();
      return;
    }
    // Already auto-reset once this tab and still crashing → stop, show manual card.
    if (hasAutoReset()) {
      this.setState({ phase: 'failed' });
      return;
    }
    // First non-chunk boot crash → clear app state once and reload. Most boot
    // crashes are corrupt/old-shape localStorage; this recovers without a logout.
    markAutoReset();
    clearAppNamespace();
    window.location.reload();
  }

  private resetAndReload = (): void => {
    clearAppNamespace();
    try {
      sessionStorage.removeItem(AUTO_RESET_FLAG);
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.phase === 'recovering') {
      // Reset + reload is firing from componentDidCatch; show a brief note rather
      // than a blank frame (and never the children, which would re-throw).
      return (
        <div
          className="screen"
          style={{ display: 'grid', placeItems: 'center', minHeight: '60vh', padding: 24, textAlign: 'center' }}
        >
          <p style={{ color: 'var(--ink-soft)', fontFamily: 'var(--ff-sans, Inter, sans-serif)' }}>
            Восстанавливаем…
          </p>
        </div>
      );
    }
    if (this.state.phase === 'failed') {
      return (
        <div
          className="screen"
          style={{ display: 'grid', placeItems: 'center', minHeight: '70vh', padding: 24, textAlign: 'center' }}
        >
          <div className="state-card" style={{ maxWidth: 380 }}>
            <h2 style={{ fontFamily: 'var(--ff-display)', marginTop: 0 }}>Не удалось загрузить студию</h2>
            <p style={{ color: 'var(--ink-soft)', lineHeight: 1.5, margin: '0 0 16px' }}>
              Похоже, в браузере остались несовместимые данные. Сбрось их и перезагрузи — вход и
              прогресс сохранятся, прогресс подтянется с сервера.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button type="button" className="btn-primary" onClick={this.resetAndReload}>
                Сбросить и перезагрузить
              </button>
              <button
                type="button"
                className="btn-secondary"
                style={{ background: 'var(--surface)', border: '1.5px solid var(--ink)', borderRadius: 'var(--r-sm)', padding: '8px 16px' }}
                onClick={() => window.location.reload()}
              >
                Просто обновить
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
